import { defineStore } from "pinia";
import { computed, shallowRef } from "vue";
import {
  DEFAULT_SECTION_ROUTES,
  type EditorSection,
} from "../router/projectPaths";
import { dispatchInto } from "../lib/tea";
import {
  initialEditorNavModel,
  updateEditorNav,
  type EditorNavMsg,
  type NavSection,
} from "./useEditorNavStore.update";

export type { NavSection };

/**
 * In-session sticky destinations for the outline ribbon. Updated on every
 * matched editor navigation; not persisted on online (URL is authoritative).
 *
 * TEA: pure Model/Msg in `useEditorNavStore.update.ts`. No Cmds, so every
 * mutator takes effect synchronously.
 */
export const useEditorNavStore = defineStore("editorNav", () => {
  const model = shallowRef(initialEditorNavModel);

  function send(msg: EditorNavMsg): void {
    void dispatchInto(model, msg, updateEditorNav, () => {});
  }

  const lastRouteBySection = computed(() => model.value.lastRouteBySection);

  function rememberPath(fullPath: string): void {
    send({ type: "RememberPath", fullPath });
  }

  function routeForSection(section: NavSection): string {
    return (
      model.value.lastRouteBySection[section] ?? DEFAULT_SECTION_ROUTES[section]
    );
  }

  function rememberSectionRoute(section: EditorSection, path: string): void {
    send({ type: "RememberSectionRoute", section, path });
  }

  return {
    lastRouteBySection,
    rememberPath,
    routeForSection,
    rememberSectionRoute,
  };
});
